/**
 * ContactAvatar — shared rendering primitive for contact identity across the
 * GHL dashboard (live feed, qualifications, follow-ups, detail pages).
 *
 * Renders a colored avatar with initials + optionally the full name and a
 * truncated contact ID as a subtitle. The avatar color is derived
 * deterministically from the name, so the same person always gets the same
 * color across every view — useful for quick visual identification.
 */

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

interface ContactAvatarProps {
  readonly firstName?: string | null
  readonly lastName?: string | null
  readonly fullName?: string | null
  readonly contactId?: string | null
  readonly size?: 'sm' | 'md' | 'lg'
  /** If true, renders name + contact ID next to the avatar. */
  readonly showName?: boolean
  /** If provided, shown as a subtitle under the name (e.g. an email). */
  readonly subtitle?: string | null
  readonly className?: string
}

// Small curated palette — avoids garish colors, sticks to Tailwind's slate-500 family feel
const AVATAR_COLORS = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-pink-100 text-pink-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
] as const

const SIZE_CLASSES = {
  sm: { avatar: 'h-6 w-6 text-[10px]', name: 'text-sm', id: 'text-xs' },
  md: { avatar: 'h-9 w-9 text-xs', name: 'text-sm font-medium', id: 'text-xs' },
  lg: { avatar: 'h-14 w-14 text-lg', name: 'text-xl font-semibold', id: 'text-sm' },
} as const

/**
 * Deterministic color pick: hash the seed string, modulo palette length.
 * Same name → same color across all views.
 */
function pickColor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

/**
 * Derive initials from the available name fields, in priority order:
 *   1. firstName[0] + lastName[0] (standard case)
 *   2. first 2 chars of fullName split on space
 *   3. first char of fullName
 *   4. "?" (no name at all)
 */
function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  fullName?: string | null
): string {
  if (firstName && lastName) {
    return (firstName[0] + lastName[0]).toUpperCase()
  }
  if (firstName) return firstName[0].toUpperCase()
  if (fullName) {
    const parts = fullName.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return fullName[0].toUpperCase()
  }
  return '?'
}

function buildDisplayName(
  firstName?: string | null,
  lastName?: string | null,
  fullName?: string | null
): string | null {
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim()
  if (joined) return joined
  if (fullName?.trim()) return fullName.trim()
  return null
}

function truncateId(id: string, max = 12): string {
  return id.length <= max ? id : `${id.slice(0, max)}…`
}

export function ContactAvatar({
  firstName,
  lastName,
  fullName,
  contactId,
  size = 'md',
  showName = false,
  subtitle,
  className,
}: ContactAvatarProps) {
  const displayName = buildDisplayName(firstName, lastName, fullName)
  const initials = getInitials(firstName, lastName, fullName)
  const sizeClasses = SIZE_CLASSES[size]

  // Color seed: prefer name (stable across renders), fall back to contactId
  const colorSeed = displayName ?? contactId ?? 'unknown'
  const colorClass = pickColor(colorSeed)

  const avatar = (
    <Avatar className={cn(sizeClasses.avatar, 'shrink-0')}>
      <AvatarFallback className={cn(colorClass, 'font-semibold')}>
        {initials}
      </AvatarFallback>
    </Avatar>
  )

  if (!showName) {
    return <div className={className}>{avatar}</div>
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {avatar}
      <div className="min-w-0 flex flex-col">
        <span className={cn(sizeClasses.name, 'truncate')}>
          {displayName ?? <span className="text-muted-foreground italic">Unknown</span>}
        </span>
        {subtitle ? (
          <span className={cn(sizeClasses.id, 'text-muted-foreground truncate')}>
            {subtitle}
          </span>
        ) : contactId ? (
          <span className={cn(sizeClasses.id, 'text-muted-foreground font-mono truncate')}>
            {truncateId(contactId)}
          </span>
        ) : null}
      </div>
    </div>
  )
}
